import React, { useEffect, useRef, useState } from "react";
import AuthServices from "../../api/AuthServices";
import { useTranslation } from "react-i18next";
import { LanguageIcon } from "../../icons";
import clsx from "clsx";
import { useMedia } from "../../hook/useMedia";

const LANGUAGE = {
  "en-US": "en",
  en: "en",
  es: "es",
};

export const LanguageSelector = ({ variant = "default" }) => {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const { i18n, t } = useTranslation();
  const { isAfterTablet } = useMedia();
  const isLight = variant === "light";
  const isCompact = isLight && isAfterTablet;

  const [currentLanguage, setCurrentLanguage] = useState({
    id: 1,
    name: "English",
    code: "en",
  });
  const [languageList, setLanguageList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    AuthServices.getLanguages().then((res) => {
      setLanguageList([...res?.data]);
      const lang =
        res?.data?.length &&
        res?.data?.find((el) => el?.code === LANGUAGE[i18n.language]);
      setCurrentLanguage(lang);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const selectedIndex = languageList.findIndex(
      (item) => item.code === currentLanguage?.code
    );
    const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;
    optionRefs.current[focusIndex]?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, languageList, currentLanguage]);

  const handleChange = async (data) => {
    i18n.changeLanguage(data.code);
    setCurrentLanguage(data);
    setIsOpen(false);
    triggerRef.current?.focus();
    const params = {
      language: LANGUAGE[data.code],
    };
    await AuthServices.changeLanguage(params);
  };

  const handleMenuKeyDown = (event) => {
    const options = optionRefs.current.filter(Boolean);
    if (options.length === 0) {
      return;
    }
    const currentIndex = options.indexOf(document.activeElement);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      options[(currentIndex + 1) % options.length]?.focus();
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      options[(currentIndex - 1 + options.length) % options.length]?.focus();
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      options[0]?.focus();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      options[options.length - 1]?.focus();
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        "pos-relative language-selector",
        isLight && "language-selector--light"
      )}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={clsx(
          "language-selector__trigger border flex border-radius-6 cursor-pointer align-items-center",
          isCompact ? "fs-12 pa-2" : "fs-16",
          !isCompact && (isAfterTablet ? "pa-2" : "pa-6"),
          isLight ? "color-dark" : "border-dark"
        )}
        style={isLight ? { borderColor: "#2d3f52", color: "#2d3f52" } : undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? "registration-language-listbox" : undefined}
        aria-label={t("NUTRITION_COMMON_LBL_LANGUAGE_SELECTOR")}
      >
        <LanguageIcon
          height={isCompact ? 14 : 16}
          width={isCompact ? 14 : 16}
          className={isCompact ? "pr-0" : "pr-1"}
          aria-hidden
        />
        {!isCompact && t("NUTRITION_COMMON_LBL_LANGUAGE_SELECTOR")}
        <span
          className={clsx(
            "pl-1 font-weight-bold",
            "color-dark"
          )}
        >
          {isCompact
            ? currentLanguage?.code?.toUpperCase()
            : currentLanguage?.name}
        </span>
      </button>
      {isOpen && (
        <div
          className={clsx(
            "language-selector__menu pos-absolute mt-1 ptb-2 plr-2 border-radius-4 border-dark border",
            isCompact ? "fs-12" : "fs-16"
          )}
          role="listbox"
          id="registration-language-listbox"
          aria-label={t("NUTRITION_COMMON_LBL_LANGUAGE_SELECTOR")}
          onKeyDown={handleMenuKeyDown}
        >
          {languageList.length > 0 &&
            languageList.map((el, index) => {
              const isSelected = el.code === currentLanguage?.code;
              return (
                <button
                  key={el.id ?? el.code}
                  type="button"
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  className={clsx(
                    "cursor-pointer languagePopup",
                    isSelected && "languagePopup--selected"
                  )}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleChange(el)}
                >
                  <span>{el.name}</span>
                  {isSelected ? (
                    <span aria-hidden>✓</span>
                  ) : null}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};
